/** GitHubReleaseList — table of GitHub releases with a "Récupérer" action.
 *
 *  Delegates the actual pipeline (fetch → dry-run → install) to the parent
 *  via `onFetched(upload_id)` so all state stays in UpdatesPage.
 */
import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Alert, AlertDescription } from "../ui/alert";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { RefreshCw, Github, Download, Loader2 } from "lucide-react";
import { useUpdater } from "../../hooks/useUpdater";

export default function GitHubReleaseList({ onFetched, disabled = false }) {
  const { listGitHubReleases, fetchGitHubRelease, extractDetail } = useUpdater();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busyTag, setBusyTag] = useState(null);

  const refresh = async () => {
    setLoading(true); setError(null);
    try {
      const r = await listGitHubReleases();
      setData(r);
    } catch (err) {
      setError(extractDetail(err, "Impossible de récupérer la liste des releases GitHub."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const doFetch = async (tag) => {
    setBusyTag(tag); setError(null);
    try {
      const r = await fetchGitHubRelease(tag);
      onFetched?.(r);
    } catch (err) {
      setError(extractDetail(err, `Échec de la récupération de ${tag}.`));
    } finally {
      setBusyTag(null);
    }
  };

  return (
    <Card className="p-4 space-y-4" data-testid="github-releases-list">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Github className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">Releases GitHub</span>
          {data?.config?.owner && (
            <Badge variant="outline" className="text-xs">
              {data.config.owner}/{data.config.repo}
            </Badge>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          disabled={loading || disabled}
          data-testid="btn-refresh-github"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Rafraîchir
        </Button>
      </div>

      {data?.error && (
        <Alert variant="destructive" data-testid="github-error">
          <AlertDescription>
            <span className="font-medium">[{data.error.code}]</span> {data.error.message_fr}
          </AlertDescription>
        </Alert>
      )}
      {data?.warnings?.length > 0 && data.warnings.map((w, i) => (
        <Alert key={i}><AlertDescription className="text-xs">{w}</AlertDescription></Alert>
      ))}
      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      {!loading && data && !data.error && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tag</TableHead>
              <TableHead>Nom</TableHead>
              <TableHead>Publié le</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data.releases || []).map((r) => (
              <TableRow key={r.tag}>
                <TableCell className="font-mono text-xs">{r.tag}</TableCell>
                <TableCell className="text-sm">{r.name}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {r.published_at ? new Date(r.published_at).toLocaleString() : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    disabled={busyTag === r.tag || disabled}
                    onClick={() => doFetch(r.tag)}
                    data-testid={`github-fetch-${r.tag}`}
                  >
                    {busyTag === r.tag
                      ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Récupération…</>
                      : <><Download className="h-4 w-4 mr-2" /> Récupérer</>}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {(data.releases || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                  Aucune release ne matche le pattern autorisé ({data.config?.tag_pattern}).
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
